import React from 'react';
import styled, { keyframes } from 'styled-components';

// ─── Sparkle SVG ─────────────────────────────────────────────────────────────

export const SparkleIcon: React.FC<React.SVGProps<SVGSVGElement>> = props => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M12 2L13.5 9L20 10.5L13.5 12L12 19L10.5 12L4 10.5L10.5 9L12 2Z" />
    <path d="M19 13.5L19.8 16.7L23 17.5L19.8 18.3L19 21.5L18.2 18.3L15 17.5L18.2 16.7L19 13.5Z" />
  </svg>
);

export const SparkleIconSmall: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2L13.5 9L20 10.5L13.5 12L12 19L10.5 12L4 10.5L10.5 9L12 2Z" />
  </svg>
);

export const SendIcon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
  </svg>
);

// ─── Animations ───────────────────────────────────────────────────────────────

export const slideIn = keyframes`
  from { transform: translateX(100%); opacity: 0; }
  to   { transform: translateX(0);    opacity: 1; }
`;

export const blink = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0; }
`;

export const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

// ─── Styled components ────────────────────────────────────────────────────────

export const PageShell = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  height: 70vh; /* fallback; the exact height (viewport − header) is set at runtime */
  overflow: hidden;
  background: ${props => props.theme.colors.neutralWhite};
`;

export const PageInner = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: ${props => props.theme.pxToRem(1120)};
  height: 100%;
  min-height: 0;
  padding: 0 ${props => props.theme.pxToRem(24)};
`;

export const PageTitleBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  column-gap: ${props => props.theme.pxToRem(8)};
  padding: ${props => props.theme.pxToRem(20)} 0 ${props => props.theme.pxToRem(6)};
  color: ${props => props.theme.colors.primaryPurple};
  flex-shrink: 0;
`;

export const PageTitleText = styled.h1`
  margin: 0;
  font-family: ${props => props.theme.fontFamily};
  font-size: ${props => props.theme.fontSizes.xl};
  font-weight: ${props => props.theme.fontWeights.bold};
  color: ${props => props.theme.colors.neutralGrey8};
`;

export const PageTitleBeta = styled.span`
  margin-left: ${props => props.theme.pxToRem(2)};
  padding: ${props => props.theme.pxToRem(1)} ${props => props.theme.pxToRem(6)};
  border-radius: ${props => props.theme.pxToRem(8)};
  background: ${props => props.theme.colors.primaryPurple};
  color: ${props => props.theme.colors.neutralWhite};
  font-family: ${props => props.theme.fontFamily};
  font-size: ${props => props.theme.fontSizes.xxs};
  font-weight: ${props => props.theme.fontWeights.bold};
  line-height: 1.4;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

// Compact "used / limit" pill shown under the header in both page and overlay
// modes. Turns red as the caller nears the 24h cap.
export const UsageBar = styled.div`
  display: flex;
  justify-content: center;
  padding: ${props => props.theme.pxToRem(4)} ${props => props.theme.pxToRem(24)} 0;
  flex-shrink: 0;
`;

export const UsageBadge = styled.span<{ warn?: boolean }>`
  font-family: ${props => props.theme.fontFamily};
  font-size: ${props => props.theme.fontSizes.xxs};
  font-weight: ${props => props.theme.fontWeights.medium};
  color: ${props => (props.warn ? '#c0392b' : props.theme.colors.neutralGrey5)};
  background: ${props => props.theme.colors.neutralGrey1};
  border: 1px solid ${props => (props.warn ? '#c0392b' : props.theme.colors.neutralGrey2)};
  border-radius: ${props => props.theme.pxToRem(12)};
  padding: ${props => props.theme.pxToRem(2)} ${props => props.theme.pxToRem(10)};
  cursor: default;
  white-space: nowrap;
`;

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1200;
  display: flex;
  justify-content: flex-end;
  animation: ${fadeIn} 0.2s ease;
`;

export const Panel = styled.div`
  display: flex;
  flex-direction: column;
  width: 60vw;
  max-width: ${props => props.theme.pxToRem(880)};
  height: 100%;
  background: ${props => props.theme.colors.neutralWhite};
  box-shadow: -4px 0 32px rgba(0, 0, 0, 0.18);
  animation: ${slideIn} 0.28s cubic-bezier(0.4, 0, 0.2, 1);

  ${props => props.theme.mediaQueries.mobileOnly} {
    width: 100vw;
  }
`;

export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${props => props.theme.pxToRem(0)} ${props => props.theme.pxToRem(24)};
  height: ${props => props.theme.pxToRem(56)};
  background: linear-gradient(to right, ${props => props.theme.colors.primaryPurple}, #9c2463);
  flex-shrink: 0;
`;

export const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  column-gap: ${props => props.theme.pxToRem(8)};
  color: ${props => props.theme.colors.neutralWhite};
`;

export const HeaderTitle = styled.span`
  font-family: ${props => props.theme.fontFamily};
  font-size: ${props => props.theme.fontSizes.m};
  font-weight: ${props => props.theme.fontWeights.medium};
  color: ${props => props.theme.colors.neutralWhite};
`;

export const HeaderBetaTag = styled.span`
  margin-left: ${props => props.theme.pxToRem(4)};
  padding: ${props => props.theme.pxToRem(1)} ${props => props.theme.pxToRem(6)};
  border-radius: ${props => props.theme.pxToRem(8)};
  background: ${props => props.theme.colors.neutralWhite};
  color: ${props => props.theme.colors.primaryPurple};
  font-family: ${props => props.theme.fontFamily};
  font-size: ${props => props.theme.fontSizes.xxs};
  font-weight: ${props => props.theme.fontWeights.bold};
  line-height: 1.4;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const CloseBtn = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.neutralWhite};
  font-size: ${props => props.theme.fontSizes.l};
  line-height: 1;
  cursor: pointer;
  padding: ${props => props.theme.pxToRem(4)};
  opacity: 0.8;

  &:hover { opacity: 1; }
`;

// ── Welcome view ──────────────────────────────────────────────────────────────

export const WelcomeArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${props => props.theme.pxToRem(48)} ${props => props.theme.pxToRem(40)} ${props => props.theme.pxToRem(24)};

  /* keep children at natural height (let the area scroll) rather than shrinking
     them — shrinking squashed the heading and overlapped the subtitle */
  > * {
    flex-shrink: 0;
  }
  color: ${props => props.theme.colors.primaryPurple};

  ${props => props.theme.mediaQueries.mobileOnly} {
    padding: ${props => props.theme.pxToRem(32)} ${props => props.theme.pxToRem(20)} ${props => props.theme.pxToRem(16)};
  }
`;

export const WelcomeHeading = styled.h1`
  font-family: ${props => props.theme.fontFamily};
  font-size: ${props => props.theme.fontSizes.l};
  font-weight: ${props => props.theme.fontWeights.medium};
  color: ${props => props.theme.colors.neutralGrey8};
  margin: 0 0 ${props => props.theme.pxToRem(8)};
  text-align: center;

  ${props => props.theme.mediaQueries.mobileOnly} {
    font-size: ${props => props.theme.fontSizes.m};
  }
`;

export const WelcomeSubtitle = styled.p`
  font-family: ${props => props.theme.fontFamily};
  font-size: ${props => props.theme.fontSizes.s};
  color: ${props => props.theme.colors.neutralGrey5};
  margin: 0 0 ${props => props.theme.pxToRem(32)};
  text-align: center;
`;

export const SuggestionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.pxToRem(12)};
  width: 100%;
  max-width: ${props => props.theme.pxToRem(720)};

  ${props => props.theme.mediaQueries.mobileOnly} {
    grid-template-columns: 1fr;
  }
`;

export const SuggestionCard = styled.button`
  display: flex;
  align-items: flex-start;
  column-gap: ${props => props.theme.pxToRem(8)};
  background: ${props => props.theme.colors.neutralGrey1};
  border: 1px solid ${props => props.theme.colors.neutralGrey2};
  border-radius: ${props => props.theme.pxToRem(10)};
  padding: ${props => props.theme.pxToRem(14)} ${props => props.theme.pxToRem(16)};
  font-family: ${props => props.theme.fontFamily};
  font-size: ${props => props.theme.fontSizes.xs};
  color: ${props => props.theme.colors.neutralGrey8};
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;

  &:hover {
    background: ${props => props.theme.colors.neutralGrey2};
    border-color: ${props => props.theme.colors.primaryPurple};
    color: ${props => props.theme.colors.primaryPurple};
  }
`;

export const SuggestionCardArrow = styled.span`
  color: ${props => props.theme.colors.primaryPurple};
  flex-shrink: 0;
  font-size: ${props => props.theme.fontSizes.s};
`;

// ── Chat view ─────────────────────────────────────────────────────────────────

export const MessagesArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: ${props => props.theme.pxToRem(24)} ${props => props.theme.pxToRem(32)};
  display: flex;
  flex-direction: column;
  row-gap: ${props => props.theme.pxToRem(16)};

  > * {
    flex-shrink: 0;
  }

  ${props => props.theme.mediaQueries.mobileOnly} {
    padding: ${props => props.theme.pxToRem(16)};
  }
`;

export const MessageGroup = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${props => props.theme.pxToRem(8)};
`;

export const BubbleRow = styled.div<{ isUser: boolean }>`
  display: flex;
  align-items: flex-start;
  column-gap: ${props => props.theme.pxToRem(8)};
  justify-content: ${props => (props.isUser ? 'flex-end' : 'flex-start')};
`;

export const AvatarDot = styled.div`
  width: ${props => props.theme.pxToRem(28)};
  height: ${props => props.theme.pxToRem(28)};
  border-radius: 50%;
  background: linear-gradient(135deg, ${props => props.theme.colors.primaryPurple}, #9c2463);
  color: ${props => props.theme.colors.neutralWhite};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: ${props => props.theme.pxToRem(2)};
`;

export const Bubble = styled.div<{ isUser: boolean }>`
  max-width: 72%;
  padding: ${props => props.theme.pxToRem(12)} ${props => props.theme.pxToRem(16)};
  border-radius: ${props => props.theme.pxToRem(14)};
  font-family: ${props => props.theme.fontFamily};
  font-size: ${props => props.theme.fontSizes.xs};
  line-height: 1.6;
  word-break: break-word;

  ${({ isUser, theme }) =>
    isUser
      ? `
        background: ${theme.colors.primaryPurple};
        color: ${theme.colors.neutralWhite};
        border-bottom-right-radius: ${theme.pxToRem(4)};
        white-space: pre-wrap;
      `
      : `
        background: ${theme.colors.neutralGrey1};
        color: ${theme.colors.neutralGrey8};
        border-bottom-left-radius: ${theme.pxToRem(4)};
      `}
`;

export const MarkdownBody = styled.div`
  font-family: ${props => props.theme.fontFamily};
  font-size: ${props => props.theme.fontSizes.xs};
  line-height: 1.6;

  p { margin: 0 0 ${props => props.theme.pxToRem(8)}; &:last-child { margin-bottom: 0; } }
  h1, h2, h3, h4 {
    font-weight: ${props => props.theme.fontWeights.bold};
    margin: ${props => props.theme.pxToRem(12)} 0 ${props => props.theme.pxToRem(4)};
    &:first-child { margin-top: 0; }
  }
  ul, ol { margin: 0 0 ${props => props.theme.pxToRem(8)}; padding-left: ${props => props.theme.pxToRem(20)}; }
  li { margin-bottom: ${props => props.theme.pxToRem(2)}; }
  strong { font-weight: ${props => props.theme.fontWeights.bold}; }
  a {
    color: ${props => props.theme.colors.primaryPurple};
    font-weight: ${props => props.theme.fontWeights.bold};
    text-decoration: underline;
    text-underline-offset: 2px;
    &:hover { color: #9c2463; text-decoration-thickness: 2px; }
    &:focus-visible { outline: 2px solid ${props => props.theme.colors.primaryPurple}; outline-offset: 2px; }
  }
  code {
    background: ${props => props.theme.colors.neutralGrey2};
    border-radius: ${props => props.theme.pxToRem(3)};
    padding: 0 ${props => props.theme.pxToRem(4)};
    font-size: ${props => props.theme.fontSizes.xxs};
    font-family: monospace;
  }
`;

export const Cursor = styled.span`
  display: inline-block;
  width: ${props => props.theme.pxToRem(8)};
  height: ${props => props.theme.pxToRem(14)};
  background: ${props => props.theme.colors.neutralGrey4};
  vertical-align: middle;
  animation: ${blink} 1s step-end infinite;
`;

export const TypingIndicator = styled.div`
  display: flex;
  align-items: center;
  column-gap: ${props => props.theme.pxToRem(4)};
  padding: ${props => props.theme.pxToRem(4)} 0;
`;

export const TypingDot = styled.span<{ delay: number }>`
  width: ${props => props.theme.pxToRem(6)};
  height: ${props => props.theme.pxToRem(6)};
  border-radius: 50%;
  background: ${props => props.theme.colors.neutralGrey4};
  animation: ${blink} 1s step-end ${props => props.delay}s infinite;
`;

export const InlineSuggestionGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.pxToRem(8)};
  padding-left: ${props => props.theme.pxToRem(36)};
`;

export const SuggestionsLoadingRow = styled.div`
  display: flex;
  align-items: center;
  column-gap: ${props => props.theme.pxToRem(5)};
  padding-left: ${props => props.theme.pxToRem(36)};
`;

export const SuggestionsLoadingDot = styled.span<{ delay: number }>`
  width: ${props => props.theme.pxToRem(6)};
  height: ${props => props.theme.pxToRem(6)};
  border-radius: 50%;
  background: ${props => props.theme.colors.neutralGrey4};
  animation: ${blink} 1s step-end ${props => props.delay}s infinite;
`;

// ── Input area ────────────────────────────────────────────────────────────────

export const InputArea = styled.div`
  display: flex;
  align-items: flex-end;
  column-gap: ${props => props.theme.pxToRem(8)};
  margin: ${props => props.theme.pxToRem(12)} 0 ${props => props.theme.pxToRem(10)};
  padding: ${props => props.theme.pxToRem(6)} ${props => props.theme.pxToRem(8)} ${props => props.theme.pxToRem(6)}
    ${props => props.theme.pxToRem(20)};
  border: 1px solid ${props => props.theme.colors.neutralGrey3};
  border-radius: ${props => props.theme.pxToRem(16)};
  background: ${props => props.theme.colors.neutralWhite};
  box-shadow: 0 ${props => props.theme.pxToRem(1)} ${props => props.theme.pxToRem(6)} rgba(0, 0, 0, 0.08);
  transition: border-color 0.15s, box-shadow 0.15s;
  flex-shrink: 0;

  &:focus-within {
    border-color: ${props => props.theme.colors.primaryPurple};
    box-shadow: 0 ${props => props.theme.pxToRem(2)} ${props => props.theme.pxToRem(12)} rgba(0, 0, 0, 0.1);
  }

  ${props => props.theme.mediaQueries.mobileOnly} {
    margin: ${props => props.theme.pxToRem(8)} 0;
    padding-left: ${props => props.theme.pxToRem(14)};
  }
`;

export const TextareaWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  row-gap: ${props => props.theme.pxToRem(4)};
`;

export const StyledTextarea = styled.textarea`
  width: 100%;
  resize: none;
  border: none;
  background: transparent;
  padding: ${props => props.theme.pxToRem(8)} 0;
  font-family: ${props => props.theme.fontFamily};
  font-size: ${props => props.theme.fontSizes.s};
  line-height: 1.5;
  color: ${props => props.theme.colors.neutralGrey8};
  outline: none;

  &::placeholder { color: ${props => props.theme.colors.neutralGrey5}; }
  &:disabled { opacity: 0.6; }
`;

export const SendBtn = styled.button`
  width: ${props => props.theme.pxToRem(40)};
  height: ${props => props.theme.pxToRem(40)};
  border-radius: 50%;
  background: ${props => props.theme.colors.primaryPurple};
  border: none;
  color: ${props => props.theme.colors.neutralWhite};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.15s, opacity 0.15s;

  &:hover:not(:disabled) { background: #9c2463; }
  &:disabled {
    background: ${props => props.theme.colors.neutralGrey3};
    color: ${props => props.theme.colors.neutralGrey5};
    cursor: not-allowed;
  }
`;

export const Disclaimer = styled.p`
  margin: 0;
  padding: ${props => props.theme.pxToRem(0)} ${props => props.theme.pxToRem(32)} ${props => props.theme.pxToRem(12)};
  font-family: ${props => props.theme.fontFamily};
  font-size: ${props => props.theme.fontSizes.xxs};
  color: ${props => props.theme.colors.neutralGrey5};
  text-align: center;
  background: ${props => props.theme.colors.neutralWhite};
  flex-shrink: 0;

  ${props => props.theme.mediaQueries.mobileOnly} {
    padding: ${props => props.theme.pxToRem(0)} ${props => props.theme.pxToRem(16)} ${props => props.theme.pxToRem(10)};
  }
`;
